import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AuthService } from '../../services/auth.service';

export type LoginDialogMode = 'signin' | 'signup' | 'forgot';

export interface LoginDialogData {
  returnUrl?: string;
  mode?: LoginDialogMode;
}

export interface LoginDialogResult {
  ok: boolean;
  returnUrl?: string;
}

@Component({
  selector: 'app-login-dialog',
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.css']
})
export class LoginDialogComponent {
  mode: LoginDialogMode;
  loginForm: FormGroup;
  forgotForm: FormGroup;
  submitting = false;
  hidePassword = true;
  forgotEmailSent = false;

  // Transient server-side error message. Shown below the form when the last
  // submit failed; cleared on next submit, on mode switch, or when the user
  // edits any field. Deliberately NOT a form validation error — keeping the
  // submit button enabled so the user can retry the same credentials after
  // a transient backend hiccup without retyping.
  serverError: string | null = null;

  constructor(
    private fb: FormBuilder,
    private auth: AuthService,
    private dialogRef: MatDialogRef<LoginDialogComponent, LoginDialogResult>,
    @Inject(MAT_DIALOG_DATA) public data: LoginDialogData,
  ) {
    this.mode = data?.mode ?? 'signin';
    this.loginForm = this.fb.group({
      companyName: ['', Validators.required],
      identifier:  ['', Validators.required],
      password:    ['', Validators.required],
    });
    this.forgotForm = this.fb.group({
      companyName: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
    });
    this.applyModeValidators();
    this.loginForm.valueChanges.subscribe(() => {
      if (this.serverError) this.serverError = null;
    });
    this.forgotForm.valueChanges.subscribe(() => {
      if (this.serverError) this.serverError = null;
    });
  }

  switchMode(target: LoginDialogMode): void {
    if (this.mode === target || this.submitting) return;
    if (target === 'forgot') {
      this.forgotForm.patchValue({
        companyName: this.loginForm.get('companyName')?.value,
        email: this.loginForm.get('identifier')?.value,
      });
    }
    this.mode = target;
    this.serverError = null;
    this.forgotEmailSent = false;
    this.applyModeValidators();
  }

  submitForgot(): void {
    if (this.forgotForm.invalid || this.submitting) return;
    this.submitting = true;
    this.serverError = null;
    const { companyName, email } = this.forgotForm.value;

    this.auth.forgotPassword(companyName, email).subscribe({
      next: () => {
        this.submitting = false;
        this.forgotEmailSent = true;
      },
      error: () => {
        this.submitting = false;
        this.serverError = 'Service unavailable, please try again';
      },
    });
  }

  submit(): void {
    if (this.loginForm.invalid || this.submitting) return;
    this.submitting = true;
    this.serverError = null;
    const { identifier, password, companyName } = this.loginForm.value;

    // Signing up is closed (see AuthService.register and AuthController), so
    // there is no route to send this to. Nothing reaches this dialog in
    // 'signup' mode any more, but the mode still exists for the day it
    // reopens - say so plainly rather than posting into a 404, which the
    // error mapper below would have rendered as a credentials problem.
    if (this.mode !== 'signin') {
      this.submitting = false;
      this.serverError = 'Creating an account is not open. Ask an administrator to invite you.';
      return;
    }

    const obs = this.auth.login(companyName, identifier, password);

    obs.subscribe({
      next: () => {
        this.dialogRef.close({ ok: true, returnUrl: this.data?.returnUrl });
      },
      error: err => {
        this.submitting = false;
        this.serverError = this.errorMessageFor(err);
      },
    });
  }

  private applyModeValidators(): void {
    const passwordCtrl = this.loginForm.get('password')!;
    if (this.mode === 'signup') {
      passwordCtrl.setValidators([Validators.required, Validators.minLength(8)]);
    } else {
      passwordCtrl.setValidators([Validators.required]);
    }
    passwordCtrl.updateValueAndValidity();
  }

  private errorMessageFor(err: { status?: number; error?: { message?: string } }): string {
    if (this.mode === 'signin') {
      if (err.status === 401) return 'Invalid company, email, or password';
      return 'Service unavailable, please try again';
    }
    if (err.status === 409) return err.error?.message ?? 'A company with that name already exists';
    if (err.status === 400) return err.error?.message ?? 'Check your inputs and try again';
    return 'Service unavailable, please try again';
  }
}
